import React, { useCallback, useEffect, useState } from "react";
import { useIdleTimer } from "react-idle-timer";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from "@mui/material";
import { useAccount } from "src/contexts/accountContext";
import { useTranslation } from 'react-i18next';

const DEFAULT_TIMEOUT_MINUTES = 30; // production-friendly idle window
const DEFAULT_COUNTDOWN_SECONDS = 120; // give user enough time to react

function readEnvInt(name, fallback) {
  const raw = (process.env[name] || '').trim();
  const parsed = Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

const PROD_TIMEOUT = readEnvInt('REACT_APP_IDLE_TIMEOUT_MINUTES', DEFAULT_TIMEOUT_MINUTES) * 60 * 1000;
const PROD_COUNTDOWN = readEnvInt('REACT_APP_IDLE_COUNTDOWN_SECONDS', DEFAULT_COUNTDOWN_SECONDS);

const InactivityTimer = ({ timeout = PROD_TIMEOUT, countdownTime = PROD_COUNTDOWN }) => {
  const [isInactive, setIsInactive] = useState(false);
  const [remainingTime, setRemainingTime] = useState(countdownTime);
  const accountContext = useAccount();
  const { t } = useTranslation('common');

  const isLoggedIn = accountContext?.isLoggedIn;

  const handleOnIdle = useCallback(() => {
    if (isLoggedIn) {
      setIsInactive(true);
      setRemainingTime(countdownTime);
    }
  }, [isLoggedIn, countdownTime]);

  const handleLogout = useCallback(() => {
    accountContext.logout();
    setIsInactive(false);
    window.location.assign("/");
  }, [accountContext]);

  const { reset } = useIdleTimer({
    timeout,
    onIdle: handleOnIdle,
    debounce: 500,
  });

  const handleStayLoggedIn = useCallback(() => {
    setIsInactive(false);
    reset();
  }, [reset]);

  useEffect(() => {
    let interval;
    if (isInactive) {
      interval = setInterval(() => {
        setRemainingTime((prevTime) => {
          if (prevTime <= 1) {
            handleLogout();
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isInactive, handleLogout]);

  if (!isLoggedIn) return null;

  return (
    <Dialog open={isInactive} onClose={handleStayLoggedIn}>
      <DialogTitle>
        {t('inactivity.title', 'Inactivity Warning')}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t(
            'inactivity.message',
            'You have been inactive for a while. You will be logged out in {{seconds}} seconds.',
            { seconds: remainingTime }
          )}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleLogout} color="secondary">
          {t('inactivity.logout', 'Logout')}
        </Button>
        <Button onClick={handleStayLoggedIn} color="primary" autoFocus>
          {t('inactivity.stayLoggedIn', 'Stay Logged In')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InactivityTimer;
